import React, { createContext, useContext, useState, useEffect } from 'react';
import envConfig from '../config/envConfig';

// Create configuration context
const ConfigContext = createContext();

// Configuration provider component
export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  const maxRetries = 3;
  const retryDelays = [2000, 4000, 8000]; // Progressive delays

  const loadConfiguration = async (attempt = 0) => {
    try {
      setLoading(true);
      setError(null);

      // Use environment-based configuration (instant loading)
      const configuration = envConfig.initializeConfig();
      
      setConfig(configuration);
      setLoading(false);
      setRetryCount(0);
    } catch (err) {
      console.error('❌ Failed to load configuration:', err);
      setError(err.message || 'Configuration initialization failed');
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfiguration();
  }, []);

  const contextValue = {
    config,
    loading,
    error,
    retryCount,
    reload: () => loadConfiguration()
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

// Hook to use configuration
export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

// Specific configuration hooks for different sections
export const useApiConfig = () => {
  const { config } = useConfig();
  return config?.api || envConfig.getApiConfig();
};

export const useBedrockConfig = () => {
  const { config } = useConfig();
  return config?.bedrock || envConfig.getBedrockConfig();
};

export const useFeatureFlags = () => {
  const { config } = useConfig();
  return config?.features || envConfig.getFeatureFlags();
};

export const useThemeConfig = () => {
  const { config } = useConfig();
  return config?.theme || envConfig.getThemeConfig();
};

export const useChatConfig = () => {
  const { config } = useConfig();
  return config?.chat || envConfig.getChatConfig();
};

export const useMonitoringConfig = () => {
  const { config } = useConfig();
  return config?.monitoring || envConfig.getMonitoringConfig();
};

export default ConfigProvider; 